// Shared "are you set up to raid" checklist.
//
// Two surfaces render this: the tall card on the profile page
// (RosterReadinessTile) and the horizontal strip on the home page
// (PlayerReadyBar). Both call computeReadiness so a rule change lands in both
// places at once and the two can never disagree about whether you're ready.
//
// The rows carry a `target` describing WHAT the row is about, not what happens
// when you click it. Each surface maps targets to its own handlers: the profile
// card scrolls and opens modals in place, the home strip confirms inline or
// hands off to the profile tab.

import { DEFAULT_RESET_RULE, ResetRule, getCurrentResetWeek, getNextResetWeek } from "./lib/time";
import { formatWeekStartShort } from "./lib/regionReset";

const TOTAL_AVAIL_SLOTS = 336; // 7 days x 48 half-hour slots

export type ReadyState = "done" | "attention" | "todo";

export type ReadyTarget =
  | { kind: "availability" }
  | { kind: "confirm-week"; week: "current" | "next" }
  | { kind: "raid-interests"; charId?: string }
  | { kind: "guild"; openGuildId?: string }
  | { kind: "join-guild" }
  | { kind: "squad" }
  | { kind: "bis"; charId?: string };

export interface ReadyRowData {
  key: string;
  label: string;
  value: string;
  state: ReadyState;
  /** Shown as a chip instead of `value` when the row is blocking. */
  actionLabel?: string;
  /** Wording used when this row becomes the surface's call to action. */
  ctaLabel?: string;
  /** What the row is about. Absent when there is nothing to act on. */
  target?: ReadyTarget;
  /** Overrides `target` for the call to action, when browsing and fixing differ. */
  ctaTarget?: ReadyTarget;
  /** Nice to have: still listed, but kept out of the score and the call to action. */
  optional?: boolean;
}

export interface ReadinessWeek {
  weekStart: string;
  slots?: string;
  confirmedAt?: string | null;
  confirmedVia?: string | null;
}

export interface ReadinessInput {
  /** Account-level default availability, as the 336-slot bool array. */
  accountAvailability: boolean[];
  weeklyAvailability: {
    currentWeek?: ReadinessWeek | null;
    nextWeek?: ReadinessWeek | null;
  };
  characters: any[];
  /** null while loading, or when the surface does not know about squads. */
  squad: { name: string; members: unknown[] } | null;
  /**
   * How many raid interests a character has picked. Resolving this needs the
   * game's option definitions, which each surface loads differently, so it
   * comes in as a function rather than raw data.
   */
  countRaidInterests: (char: any) => number;
  /**
   * The viewer's weekly reset. Decides which day the week labels below name.
   * Defaults to EU. For an account spanning regions, pass ruleForAccount().
   */
  resetRule?: ResetRule;
}

export interface ReadinessResult {
  rows: ReadyRowData[];
  syncLabel: string;
  /** The one row worth nagging about, or null when nothing is outstanding. */
  cta: { label: string; target: ReadyTarget } | null;
  /** Scored rows only: optional rows never drag the score down. */
  done: number;
  total: number;
}

export function bitstringToBoolArray(bits: string | null | undefined): boolean[] {
  const arr = new Array(TOTAL_AVAIL_SLOTS).fill(false);
  if (!bits) return arr;
  const len = Math.min(bits.length, TOTAL_AVAIL_SLOTS);
  for (let i = 0; i < len; i++) arr[i] = bits[i] === "1";
  return arr;
}

// Thin wrappers over the shared reset helpers. These were a private copy of the
// "Wednesday 00:00 UTC" rule, which is EU-shaped and a day out for US realms.
// The names are kept because several surfaces import them; pass a `rule` to get
// the viewer's real reset (see src/lib/regionReset.ts), or omit it for EU.
export function getCurrentWednesday(rule: ResetRule = DEFAULT_RESET_RULE): string {
  return getCurrentResetWeek(rule);
}

export function getNextWednesday(rule: ResetRule = DEFAULT_RESET_RULE): string {
  return getNextResetWeek(rule);
}

/**
 * Renders a week label as the day that week ACTUALLY started.
 *
 * `iso` is a week_start label, which is a Wednesday-UTC date in every region.
 * Printing it raw told a US player "This week (Aug 12)" when their week opened
 * Aug 11 — a day late, every week. Pass the viewer's rule and it resolves to the
 * real reset day; omit it and you get EU, which is what this always did.
 */
export function shortDay(iso: string, rule: ResetRule = DEFAULT_RESET_RULE): string {
  return formatWeekStartShort(iso, rule);
}

export function computeReadiness({
  accountAvailability,
  weeklyAvailability,
  characters,
  squad,
  countRaidInterests,
  resetRule = DEFAULT_RESET_RULE,
}: ReadinessInput): ReadinessResult {
  // The weekly override wins when it exists, otherwise the account default
  // is what the solver will read for this reset.
  const weekSlots = weeklyAvailability.currentWeek?.slots;
  const slotCount = weekSlots
    ? bitstringToBoolArray(weekSlots).filter(Boolean).length
    : accountAvailability.filter(Boolean).length;
  const hours = Math.round(slotCount / 2);

  const currentStart = weeklyAvailability.currentWeek?.weekStart || getCurrentWednesday(resetRule);
  const nextStart = weeklyAvailability.nextWeek?.weekStart || getNextWednesday(resetRule);
  const currentAnswered = !!weeklyAvailability.currentWeek?.confirmedAt;
  const nextAnswered = !!weeklyAvailability.nextWeek?.confirmedAt;
  const currentDeclined = weeklyAvailability.currentWeek?.confirmedVia === "declined";
  const nextDeclined = weeklyAvailability.nextWeek?.confirmedVia === "declined";

  const totalChars = characters.length;
  const guilded = characters.filter((c: any) => c.guildId).length;
  const raidReady = characters.filter((c: any) => countRaidInterests(c) > 0).length;
  const firstCharNotRaidReady = characters.find((c: any) => countRaidInterests(c) === 0);
  const withBis = characters.filter((c: any) => (c.bisCount ?? 0) > 0).length;
  const firstCharMissingBis = characters.find((c: any) => (c.bisCount ?? 0) === 0);
  const firstUnguildedChar = characters.find((c: any) => !c.guildId);
  const firstGuildedChar = characters.find((c: any) => c.guildId);

  const rows: ReadyRowData[] = [];

  rows.push(
    slotCount > 0
      ? { key: "avail", label: "Availability set", value: `${hours}h / week`, state: "done", target: { kind: "availability" } }
      : { key: "avail", label: "Availability set", value: "none", state: "attention", actionLabel: "Set", ctaLabel: "Set availability", target: { kind: "availability" } }
  );

  rows.push({
    key: "week-current",
    label: `This week (${shortDay(currentStart, resetRule)})`,
    value: currentAnswered ? (currentDeclined ? "sitting out" : "confirmed") : "unanswered",
    state: currentAnswered ? "done" : "attention",
    actionLabel: "Confirm",
    ctaLabel: `Confirm ${shortDay(currentStart, resetRule)}`,
    target: currentAnswered ? { kind: "availability" } : { kind: "confirm-week", week: "current" },
  });

  rows.push({
    key: "week-next",
    label: `Next week (${shortDay(nextStart, resetRule)})`,
    value: nextAnswered ? (nextDeclined ? "sitting out" : "confirmed") : "unanswered",
    // Only one week nags at a time: next week waits its turn.
    state: nextAnswered ? "done" : currentAnswered ? "attention" : "todo",
    actionLabel: "Confirm",
    ctaLabel: `Confirm ${shortDay(nextStart, resetRule)}`,
    target: nextAnswered ? { kind: "availability" } : { kind: "confirm-week", week: "next" },
  });

  rows.push({
    key: "raid-ready",
    label: "Ready to raid",
    value: totalChars === 0 ? "no toons" : `${raidReady} of ${totalChars}`,
    state: totalChars === 0 ? "todo" : raidReady === 0 ? "attention" : raidReady === totalChars ? "done" : "todo",
    actionLabel: "Pick",
    ctaLabel: firstCharNotRaidReady ? `Raids for ${firstCharNotRaidReady.name}` : "Pick your raids",
    target: firstCharNotRaidReady
      ? { kind: "raid-interests", charId: firstCharNotRaidReady.id }
      : characters[0]
        ? { kind: "raid-interests", charId: characters[0].id }
        : undefined,
  });

  rows.push({
    key: "guild",
    label: "Guilded",
    value: totalChars === 0 ? "no toons" : `${guilded} of ${totalChars}`,
    state: totalChars === 0 ? "todo" : guilded === 0 ? "attention" : guilded === totalChars ? "done" : "todo",
    actionLabel: "Join",
    ctaLabel: firstUnguildedChar ? `Guild ${firstUnguildedChar.name}` : "Join a guild",
    // Browsing and fixing part ways here: the row opens the guild you're
    // already in, the call to action goes where you can guild the stragglers.
    target: firstGuildedChar ? { kind: "guild", openGuildId: firstGuildedChar.guildId } : { kind: "join-guild" },
    ctaTarget: { kind: "join-guild" },
  });

  rows.push({
    key: "squad",
    label: "Squad",
    value: squad ? `${squad.name} · ${squad.members.length}` : "not joined",
    state: squad ? "done" : "todo",
    optional: true,
    target: { kind: "squad" },
  });

  rows.push({
    key: "bis",
    label: "BiS lists",
    value: totalChars === 0 ? "no toons" : `${withBis} of ${totalChars}`,
    state: totalChars === 0 ? "todo" : withBis === totalChars ? "done" : "todo",
    optional: true,
    target: firstCharMissingBis ? { kind: "bis", charId: firstCharMissingBis.id } : undefined,
  });

  // Last sync is whatever the most recently refreshed character reports.
  const syncTimes = characters
    .map((c: any) => c.metadata?.wcl_last_updated || c.metadata?.equipment_last_updated)
    .filter(Boolean)
    .map((t: string) => new Date(t).getTime())
    .filter((t: number) => !Number.isNaN(t));
  let syncLabel = "Never synced";
  if (syncTimes.length) {
    const days = Math.floor((Date.now() - Math.max(...syncTimes)) / 86400000);
    syncLabel = days <= 0 ? "Synced today" : days === 1 ? "Synced yesterday" : `Synced ${days} days ago`;
  }

  // Optional rows never become the call to action: it only ever points at
  // something that's actually holding you back.
  const actionable = rows.filter((r) => !r.optional && r.target);
  const nextUp = actionable.find((r) => r.state === "attention") || actionable.find((r) => r.state === "todo");
  const ctaTarget = nextUp?.ctaTarget || nextUp?.target;
  const cta = nextUp && ctaTarget ? { label: nextUp.ctaLabel || nextUp.label, target: ctaTarget } : null;

  const scored = rows.filter((r) => !r.optional);

  return {
    rows,
    syncLabel,
    cta,
    done: scored.filter((r) => r.state === "done").length,
    total: scored.length,
  };
}
